/**
 * Contract-agnostic SSE transport over `fetch` + `ReadableStream`.
 *
 * Everything here is independent of WHAT is being streamed: frame parsing,
 * exponential backoff with full jitter, abort semantics, mid-stream recovery.
 * `sseClient.ts` (debate) and `crawlClient.ts` (Keşif) supply only a decoder
 * and a URL.
 *
 * Why generic rather than a second copy: the debate stream and the crawl
 * stream speak the same framing because the backend encodes both through one
 * `streaming/sse.py::encode_model`. Two hand-maintained readers would drift —
 * one would learn to join multi-line `data:` payloads and the other would
 * not — and the symptom (a single event type silently vanishing) is
 * invisible to `tsc`. Same reasoning as `csrf.ts` being the only place that
 * knows the header name.
 */
import { withCsrfHeader } from "./csrf";
import { safeJsonParse, type CodecLogger } from "./eventCodec";

export interface SseReconnectPolicy {
  maxAttempts: number;
  baseDelayMs: number;
  /** Hard cap on any single delay. Default: 30 000 ms. */
  maxDelayMs?: number;
  /** Fraction of delay added as random jitter [0..1]. Default: 0.4. */
  jitterRatio?: number;
}

export const DEFAULT_RECONNECT: SseReconnectPolicy = {
  maxAttempts: 8,
  baseDelayMs: 800,
  maxDelayMs: 30_000,
  jitterRatio: 0.4,
};

/**
 * What the runner should do about a non-OK HTTP response.
 *
 * Modelled as a returned value rather than a thrown exception on purpose.
 * The previous exception-based shape had every fatal branch `throw` into the
 * SAME `catch` that implements retry, so a 401/403 — explicitly commented as
 * "no point retrying" — was in fact retried eight times over ~2 minutes and
 * `onError` fired twice. A verdict cannot be misrouted that way.
 */
export type SseResponseVerdict =
  /** Expected terminal condition. Ends the stream silently, no `onError`. */
  | { kind: "stop" }
  /** Unrecoverable. `onError` once, then throw. Never retried. */
  | { kind: "fatal"; error: Error }
  /** Recoverable. Counted against `reconnect.maxAttempts`. */
  | { kind: "retry"; error: Error };

export interface SseStreamOptions<T> {
  url: string;
  method: "GET" | "POST";
  /** Serialised as JSON for POST. Ignored for GET. */
  body?: unknown;
  headers?: Record<string, string>;
  signal: AbortSignal;
  /** Validates one decoded JSON frame. Returning `null` drops the frame. */
  decode: (raw: unknown, logger?: CodecLogger) => T | null;
  onEvent: (evt: T) => void;
  onError?: (err: Error) => void;
  /**
   * Called before each reconnect attempt so the UI can show
   * "Yeniden bağlanıyor (2/8)…" without blocking the retry.
   */
  onReconnecting?: (attempt: number, maxAttempts: number, delayMs: number) => void;
  /**
   * Classifies a non-OK response. Return `null` to accept the default
   * (transient status → retry, anything else → fatal).
   */
  onResponse?: (res: Response) => Promise<SseResponseVerdict | null>;
  reconnect?: SseReconnectPolicy;
  logger?: CodecLogger;
}

const FRAME_DELIMITER = "\n\n";

interface ParsedFrame {
  event: string | null;
  data: string;
}

function parseSseFrame(rawFrame: string): ParsedFrame | null {
  if (!rawFrame || rawFrame.startsWith(":")) return null; // keep-alive comment
  let event: string | null = null;
  const dataLines: string[] = [];
  for (const lineRaw of rawFrame.split("\n")) {
    if (lineRaw.length === 0) continue;
    if (lineRaw.startsWith(":")) continue;
    const colonIdx = lineRaw.indexOf(":");
    const field = colonIdx === -1 ? lineRaw : lineRaw.slice(0, colonIdx);
    let value = colonIdx === -1 ? "" : lineRaw.slice(colonIdx + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    switch (field) {
      case "event":
        event = value;
        break;
      case "data":
        dataLines.push(value);
        break;
      default:
        break;
    }
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

export function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    (err as { name: string }).name === "AbortError"
  );
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

async function consumeStream<T>(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  opts: SseStreamOptions<T>,
): Promise<void> {
  let buffer = "";
  const emit = (frame: string): void => {
    const parsed = parseSseFrame(frame);
    if (!parsed) return;
    const json = safeJsonParse(parsed.data);
    if (json === null) return;
    const evt = opts.decode(json, opts.logger);
    if (evt) opts.onEvent(evt);
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx = buffer.indexOf(FRAME_DELIMITER);
    while (idx !== -1) {
      emit(buffer.slice(0, idx));
      buffer = buffer.slice(idx + FRAME_DELIMITER.length);
      idx = buffer.indexOf(FRAME_DELIMITER);
    }
  }
  buffer += decoder.decode();
  const tail = buffer.trim();
  if (tail.length > 0) emit(tail);
}

/**
 * Retryable HTTP status codes: 408 Request Timeout, 425 Too Early,
 * 429 Too Many Requests, and every 5xx.
 */
export function isTransientStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

/**
 * Next backoff delay with full jitter: `min(cap, base * 2^(n-1))` plus up to
 * `jitterRatio` of that again. Avoids a reconnect thundering herd.
 */
function computeBackoff(policy: SseReconnectPolicy, attempt: number): number {
  const cap = policy.maxDelayMs ?? 30_000;
  const raw = policy.baseDelayMs * Math.pow(2, attempt - 1);
  const ceiling = Math.min(raw, cap);
  const jitter = ceiling * (policy.jitterRatio ?? 0.4) * Math.random();
  return Math.round(ceiling + jitter);
}

/**
 * Internal marker so the retry `catch` can tell "this attempt failed, try
 * again" from "this stream is over". Without a marker the two are the same
 * `Error` and the fatal path silently becomes retryable — the exact defect
 * this module's verdict type exists to prevent.
 */
class FatalSse {
  constructor(readonly error: Error) {}
}

/**
 * Reason to show the operator when the stream never opens.
 *
 * The backend already explains itself in Turkish through the shared
 * `{ok, code, message}` envelope (concurrency cap, unreachable model,
 * expired session). Falling straight through to the status line put
 * "SSE upgrade failed: HTTP 429 Too Many Requests" in a Turkish toast,
 * which tells the operator nothing they can act on.
 */
async function describeFailure(response: Response): Promise<Error> {
  let message = "";
  try {
    const body = (await response.json()) as {
      message?: unknown;
      detail?: unknown;
    } | null;
    for (const candidate of [body?.message, body?.detail]) {
      if (typeof candidate === "string" && candidate.trim()) {
        message = candidate.trim();
        break;
      }
    }
  } catch {
    /* not JSON — fall back to the status line below */
  }
  if (!message) {
    message = `Tartışma akışı başlatılamadı (HTTP ${response.status} ${response.statusText}).`;
  }
  return new Error(message);
}

/**
 * Run one SSE subscription to completion.
 *
 * Resolves when the server closes the stream cleanly or the signal aborts.
 * Rejects only on a fatal response or after `maxAttempts` exhausted retries.
 *
 * A drop in the MIDDLE of a healthy stream is treated exactly like a failed
 * connect: the server replays from its own state on reattach, so retrying is
 * always preferable to surfacing an error the user cannot act on.
 */
export async function runSseStream<T>(opts: SseStreamOptions<T>): Promise<void> {
  const policy = opts.reconnect ?? DEFAULT_RECONNECT;
  const headers: Record<string, string> = withCsrfHeader({
    Accept: "text/event-stream",
    ...(opts.method === "POST" ? { "Content-Type": "application/json" } : {}),
    ...(opts.headers ?? {}),
  });

  let attempt = 0;

  while (!opts.signal.aborted) {
    let retryError: Error | null = null;
    try {
      const response = await fetch(opts.url, {
        method: opts.method,
        headers,
        ...(opts.method === "POST"
          ? { body: JSON.stringify(opts.body ?? {}) }
          : {}),
        signal: opts.signal,
        cache: "no-store",
        credentials: "include",
      });

      if (!response.ok) {
        // `onResponse` consumes the body (409 clarification gate reads it as
        // JSON), so the diagnostic copy has to be taken before handing it over.
        const diagnostic = response.clone();
        const custom = opts.onResponse ? await opts.onResponse(response) : null;
        const verdict: SseResponseVerdict =
          custom ?? {
            kind: isTransientStatus(response.status) ? "retry" : "fatal",
            error: await describeFailure(diagnostic),
          };
        switch (verdict.kind) {
          case "stop":
            return;
          case "fatal":
            opts.onError?.(verdict.error);
            throw new FatalSse(verdict.error);
          case "retry":
            retryError = verdict.error;
            break;
        }
      } else if (!response.body) {
        // A 200 with no readable body is a proxy fault, not an application
        // decision — worth one retry cycle rather than a hard failure.
        retryError = new Error("SSE response has no body");
      } else {
        // Reset on a successful connection so a burst of 503s followed by
        // recovery does not permanently consume the retry budget.
        attempt = 0;
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        try {
          await consumeStream(reader, decoder, opts);
          return; // clean server-side EOF
        } finally {
          try {
            reader.releaseLock();
          } catch {
            /* already released */
          }
        }
      }
    } catch (err) {
      if (err instanceof FatalSse) throw err.error;
      if (isAbortError(err) || opts.signal.aborted) return;
      // Everything else — DNS failure, TLS reset, a drop partway through a
      // healthy stream — is a transport hiccup and gets the retry budget.
      retryError = err instanceof Error ? err : new Error(String(err));
    }

    // Only reachable when this attempt was classified retryable.
    attempt += 1;
    if (attempt > policy.maxAttempts) {
      const final = retryError ?? new Error("SSE bağlantısı kurulamadı.");
      opts.onError?.(final);
      throw final;
    }
    const backoff = computeBackoff(policy, attempt);
    opts.onReconnecting?.(attempt, policy.maxAttempts, backoff);
    try {
      await delay(backoff, opts.signal);
    } catch {
      return; // aborted while waiting
    }
  }
}
